import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, PackagePlus, Download, Tag } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { LabelPrintDialog } from "../components/labels/LabelPrintDialog";
import { api, errMsg } from "../lib/api";
import { rp, downloadCSV } from "../lib/format";
import { PageWrap, ProductThumb, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ProductForm } from "../components/products/ProductForm";
import { StockDialog } from "../components/products/StockDialog";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useAuth } from "../context/AuthContext";

export default function Products() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [stockF, setStockF] = useState("all");
  const [form, setForm] = useState({ open: false, product: null });
  const [stockP, setStockP] = useState(null);
  const [del, setDel] = useState(null);
  const [selected, setSelected] = useState({});
  const [labelFor, setLabelFor] = useState(null);
  const toggleSel = (id, v) => setSelected((s) => ({ ...s, [id]: v }));

  const load = useCallback(() => api.get("/products", { params: { include_inactive: true } }).then((r) => setItems(r.data)), []);
  useEffect(() => { load(); api.get("/categories").then((r) => setCats(r.data)); }, [load]);
  const catMap = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c])), [cats]);

  const list = items.filter((p) => {
    const s = q.toLowerCase();
    if (s && !(p.name.toLowerCase().includes(s) || p.barcode.includes(s) || p.sku.toLowerCase().includes(s))) return false;
    if (cat !== "all" && p.category_id !== cat) return false;
    if (stockF === "low" && p.stock > p.min_stock) return false;
    if (stockF === "out" && p.stock > 0) return false;
    return true;
  });

  const remove = async () => {
    try {
      await api.delete(`/products/${del.id}`);
      toast.success("Produk dihapus");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setDel(null);
  };

  const selList = items.filter((p) => selected[p.id]);
  const allChecked = list.length > 0 && list.every((p) => selected[p.id]);
  const toggleAll = (v) => setSelected((s) => ({ ...s, ...Object.fromEntries(list.map((p) => [p.id, v])) }));

  const exportCsv = () =>
    downloadCSV("produk.csv", [["Nama", "SKU", "Barcode", "Kategori", "Harga", "Modal", "Stok", "Satuan"],
      ...list.map((p) => [p.name, p.sku, p.barcode, catMap[p.category_id]?.name || "", p.price, p.cost, p.stock, p.unit])]);

  return (
    <PageWrap>
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Cari nama, SKU, barcode..." value={q} onChange={(e) => setQ(e.target.value)} data-scan-target="1" data-testid="products-search-input" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="w-40" data-testid="products-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Kategori</SelectItem>
              {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={stockF} onValueChange={setStockF}>
            <SelectTrigger className="w-36" data-testid="products-stock-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Stok</SelectItem>
              <SelectItem value="low">Stok Menipis</SelectItem>
              <SelectItem value="out">Stok Habis</SelectItem>
            </SelectContent>
          </Select>
          {can("labels") && <Button variant="outline" disabled={!selList.length} onClick={() => setLabelFor(selList)} data-testid="print-labels-btn"><Tag className="h-4 w-4 mr-2" />Cetak Label ({selList.length})</Button>}
          <Button variant="outline" onClick={exportCsv} data-testid="products-export-btn"><Download className="h-4 w-4 mr-2" />CSV</Button>
          {can("products") && <Button onClick={() => setForm({ open: true, product: null })} data-testid="add-product-btn"><Plus className="h-4 w-4 mr-2" />Tambah Produk</Button>}
        </div>
      </div>
      <div className="rounded-2xl border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"><Checkbox checked={allChecked} onCheckedChange={(v) => toggleAll(!!v)} data-testid="select-all-products" /></TableHead>
              <TableHead>Produk</TableHead>
              <TableHead className="hidden md:table-cell">Kategori</TableHead>
              <TableHead className="text-right">Harga</TableHead>
              <TableHead className="text-right hidden sm:table-cell">Modal</TableHead>
              <TableHead className="text-center">Stok</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((p) => (
              <TableRow key={p.id} data-testid={`product-row-${p.id}`} className={!p.active ? "opacity-50" : ""}>
                <TableCell><Checkbox checked={!!selected[p.id]} onCheckedChange={(v) => toggleSel(p.id, !!v)} data-testid={`select-product-${p.id}`} /></TableCell>
                <TableCell>
                  <div className="flex items-center gap-3 min-w-[200px]">
                    <ProductThumb product={p} color={catMap[p.category_id]?.color} className="h-10 w-10 rounded-lg text-xs shrink-0" />
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{p.name}</div>
                      <div className="text-xs text-muted-foreground font-mono">{p.barcode || "-"} · {p.sku}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell">{catMap[p.category_id]?.name || "-"}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{rp(p.price)}</TableCell>
                <TableCell className="text-right font-mono text-muted-foreground hidden sm:table-cell">{rp(p.cost)}</TableCell>
                <TableCell className="text-center">
                  <Badge variant={p.stock <= 0 ? "destructive" : p.stock <= p.min_stock ? "secondary" : "outline"} className={`font-mono ${p.stock > 0 && p.stock <= p.min_stock ? "text-warning" : ""}`}>{p.stock}</Badge>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" title="Cetak label" onClick={() => setLabelFor([p])} data-testid={`label-btn-${p.id}`}><Tag className="h-4 w-4" /></Button>
                  {can("products") && (<>
                  <Button size="icon" variant="ghost" title="Atur stok" onClick={() => setStockP(p)} data-testid={`stock-btn-${p.id}`}><PackagePlus className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" title="Edit" onClick={() => setForm({ open: true, product: p })} data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" title="Hapus" className="text-destructive" onClick={() => setDel(p)} data-testid={`delete-product-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </>)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {list.length === 0 && <Empty text="Belum ada produk." />}
      </div>
      <ProductForm open={form.open} product={form.product} cats={cats} onOpenChange={(o) => setForm((f) => ({ ...f, open: o }))} onSaved={load} />
      <LabelPrintDialog products={labelFor || []} open={!!labelFor} onOpenChange={(o) => !o && setLabelFor(null)} />
      <StockDialog product={stockP} onClose={() => setStockP(null)} onSaved={load} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Hapus produk?" description={`"${del?.name}" akan dihapus permanen.`} onConfirm={remove} />
    </PageWrap>
  );
}
